import '@/pages/admin/admin.css';

import { LoadingContainer } from '@/components/ui';
import { sysadminService, type EmpresaConDosEnCursoDto } from '@/services/sysadmin.service';
import { getAsesoriaEstadoLabel } from '@/utils/asesoriaStatus';
import { AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';

function formatDate(d?: string | null) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

// ─── Un bloque por empresa: sus asesorías EN_CURSO conflictivas + botón de corrección ────

function EmpresaConDosEnCursoCard({
  empresa,
  onCorregir,
  corrigiendo,
}: {
  empresa: EmpresaConDosEnCursoDto;
  onCorregir: (nit: string) => void;
  corrigiendo: boolean;
}) {
  // La más reciente por fechaInicio es la que quedaría EN_CURSO tras corregir — se marca para que
  // quede claro cuál se conserva y cuáles pasarían a SIN_EVALUAR.
  const masReciente = [...empresa.asesorias].sort((a, b) => b.fechaInicio.localeCompare(a.fechaInicio))[0];

  return (
    <div className="admin-card">
      <div className="admin-card-header flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-(--admin-text)">{empresa.empresaNombre}</span>
          <span className="admin-chip text-[10px] bg-(--admin-red-bg) text-(--admin-red)">
            {empresa.asesorias.length} EN_CURSO simultáneas
          </span>
        </div>
        <button
          type="button"
          disabled={corrigiendo}
          onClick={() => onCorregir(empresa.empresaId)}
          className="admin-btn text-[11px] py-1 px-2.5 disabled:opacity-50"
        >
          {corrigiendo ? 'Corrigiendo...' : 'Corregir'}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Asesor</th>
              <th>Inicio</th>
              <th>Estado</th>
              <th>Tras corregir</th>
            </tr>
          </thead>
          <tbody>
            {empresa.asesorias.map((a) => {
              const seConserva = a.asesoriaId === masReciente?.asesoriaId;
              return (
                <tr key={a.asesoriaId}>
                  <td className="text-(--admin-text2) font-mono" style={{ textAlign: 'right' }}>
                    {a.asesoriaId}
                  </td>
                  <td className="text-(--admin-text)">{a.asesorNombre}</td>
                  <td className="text-(--admin-text2) whitespace-nowrap">{formatDate(a.fechaInicio)}</td>
                  <td>
                    <span className="admin-chip text-[10px] bg-(--admin-purple-bg) text-(--admin-purple)">
                      {getAsesoriaEstadoLabel(a.estado as any)}
                    </span>
                  </td>
                  <td>
                    {seConserva ? (
                      <span className="flex items-center gap-1 text-[11px] font-medium text-(--admin-green)">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Se conserva EN_CURSO
                      </span>
                    ) : (
                      <span className="text-[11px] text-(--admin-text3)">Pasaría a Sin evaluar</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────

export function GestionAnomaliasPage() {
  const [empresas, setEmpresas] = useState<EmpresaConDosEnCursoDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [corrigiendoNit, setCorrigiendoNit] = useState<string | null>(null);
  const [corregirError, setCorregirError] = useState<string | null>(null);

  const cargar = () => {
    setIsLoading(true);
    setError(null);
    sysadminService
      .obtenerEmpresasConDosEnCurso()
      .then((res) => setEmpresas(res.data))
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar'))
      .finally(() => setIsLoading(false));
  };

  useEffect(cargar, []);

  const handleCorregir = async (nit: string) => {
    setCorrigiendoNit(nit);
    setCorregirError(null);
    try {
      const res = await sysadminService.corregirDosEnCurso(nit);
      if (res.success) {
        cargar();
      } else {
        setCorregirError('Error al corregir la empresa');
      }
    } catch (err) {
      setCorregirError(err instanceof Error ? err.message : 'Error al corregir la empresa');
    } finally {
      setCorrigiendoNit(null);
    }
  };

  if (isLoading) return <LoadingContainer text="Cargando anomalías de estado..." />;

  if (error) {
    return (
      <div className="admin-card p-4 bg-(--admin-red-bg) border-(--admin-red) text-(--admin-red)">
        <p className="text-[13px] font-medium">Error al cargar: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[18px] font-bold text-(--admin-text) tracking-tight">Anomalías de estado</h1>
        <p className="text-[12px] text-(--admin-text3)">
          Empresas con asesorías en un estado que ningún flujo del sistema espera. Primer caso: dos o más asesorías EN_CURSO al mismo tiempo
          para la misma empresa — normalmente pasa si la empresa se reasignó mientras la asesoría anterior seguía sin finalizar. Corregir
          conserva EN_CURSO la más reciente y pasa el resto a Sin evaluar, para que el nuevo asesor las revise.
        </p>
      </div>

      {corregirError && (
        <div className="flex items-center gap-2 rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-4 py-3 text-[12px] text-(--admin-red)">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {corregirError}
        </div>
      )}

      {empresas.length === 0 ? (
        <div className="admin-card p-10 text-center text-(--admin-text3)">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2 opacity-20" />
          <div className="text-[13px]">No hay empresas con esta anomalía.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {empresas.map((empresa) => (
            <EmpresaConDosEnCursoCard
              key={empresa.empresaId}
              empresa={empresa}
              onCorregir={handleCorregir}
              corrigiendo={corrigiendoNit === empresa.empresaId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
